import {AfterViewInit, Component, Input, Output, EventEmitter, OnInit, ElementRef} from '@angular/core';
import * as d3 from 'd3';
import { ColorScale, Heatmap } from 'heatmap-component'; 

@Component({
    selector: 'app-pae-view',
    templateUrl: './pae-view.component.html',
    styleUrls: ['./pae-view.component.css'],
    host: {
        '(document:keydown)': 'keyPressListener($event)'
    },
    standalone: false
})
export class PaeViewComponent implements OnInit, AfterViewInit {

  @Input() accession: string;
  @Input() paeDocUrl: string;
  @Input() imageUrl: string;
  @Input() length: number;

  @Output()
  selectedRegion:EventEmitter<Object> = new EventEmitter();

  // DOM Elements
  paeImgContainer: d3.Selection<HTMLElement, unknown, HTMLElement, any>;

  // D3 scales & Axis
  xAxis: d3.Axis<d3.NumberValue>;
  yAxis: d3.Axis<d3.NumberValue>;
  x: d3.ScaleLinear<number, number>;
  y: d3.ScaleLinear<number, number>;
 
  // Globals
  brushCtx: any;
  brushSelection: any;
  domainMax: number;
  width: number;
  height: number;

  constructor(private el:ElementRef) {}

  ngOnInit() {
    this.domainMax = this.length;
    this.renderPae("paeviewer");
  }

  ngAfterViewInit() {
    this.renderAxis();
  }

  async renderPae(divElementOrId: HTMLDivElement | string): Promise<void> {
    const uniprotId = this.accession // this.setTextContent('#uniprot-id', uniprotId);
    const pae = await this.fetchPAEMatrix(uniprotId, undefined);

    if (!pae) {
        const msg = `Failed to fetch data for ${uniprotId}.`;
        this.setTextContent('#error', `Error: ${msg}`);
        throw new Error(msg);
    }
    const seqDomain = new Array(pae.n).fill(0).map((_,i)=>i+1);
    const heatmap = Heatmap.create<number,number,number>({
        xDomain: seqDomain,
        yDomain: seqDomain,
        data: pae.data,
        x: (d, i) => i % pae.n + 1,
        y: (d, i) => Math.floor(i / pae.n) + 1,
    });
    const colorScale = ColorScale.continuous('Greens', [0, 32], [1, 0]);
    heatmap.setColor(d => colorScale(d));
    heatmap.setTooltip(null);
    heatmap.setVisualParams({ xGapRelative: 0, yGapRelative: 0 });
    heatmap.extensions.marker?.update({ freeze: true });
    heatmap.setBrushing({ enabled: true });
    heatmap.events.brush.subscribe(e => {
        const selection = e?.selection ? `Scored residue (x): ${e.selection.xFirst}-${e.selection.xLast} / Aligned residue (y): ${e.selection.yFirst}-${e.selection.yLast}` : 'None';
        const left = e.selection ? e.selection.xMinIndex / pae.n : 0;
        const width = e.selection ? (e.selection.xMaxIndex - e.selection.xMinIndex) / pae.n : 0;
        const top = e.selection ? e.selection.yMinIndex / pae.n : 0;
        const height = e.selection ? (e.selection.yMaxIndex - e.selection.yMinIndex) / pae.n : 0;
        const color = e.type === 'end' ? '#777777' : '#aaaaaa';
        this.setTextContent('#selection', selection);
        document.getElementById('xindicator')?.setAttribute('style', `left: ${left * 100}%; width: ${width * 100}%; background-color: ${color};`);
        document.getElementById('yindicator')?.setAttribute('style', `top: ${top * 100}%; height: ${height * 100}%; background-color: ${color};`);
        if (e.type==='end'){
        const selectedRegion = e.selection? [
          {
            start: e.selection.xFirst, end: e.selection.xLast 
          },
          {
            start: e.selection.yFirst, end: e.selection.yLast 
          }
        ] : [{start:0, end:0},{start:0,end:0}];;
        this.selectedRegion.emit(selectedRegion);
      }
    });
    heatmap.render(divElementOrId);
    (window as any).heatmap = heatmap;
}

  async fetchPAEMatrix(uniprotId: string, cut?: number) {

    const url = this.paeDocUrl;
    // const url = `https://alphafold.ebi.ac.uk/files/AF-${uniprotId}-F1-predicted_aligned_error_v4.json`;
    const response = await fetch(url);
    
    if (!response.ok) return undefined;
    const js = await response.json();
    let values = js[0].predicted_aligned_error as number[][];
    if (cut !== undefined) {
        values = values.slice(0, cut).map(row => row.slice(0, cut));
    }
    return {
        n: values.length,
        data: values.flatMap(x => x),
    };
  }

  /** Set text content to all HTML elements selected by `elementSelector`.
   * Example: `setTextContent('#element-to-change', 'changed text here');` */
  setTextContent(elementSelector: string, content: unknown, numberPrecision: number = 4): void {
    const elements = document.querySelectorAll(elementSelector);
    if (typeof content === 'number' && numberPrecision >= 0) content = content.toFixed(numberPrecision);
    elements.forEach(element => element.textContent = `${content}`);
  }

  formatTicks(d: number): any {
    return d > 999 ? d / 1000 + 'k' : d;
  }

  renderAxis() {

    // Selection Image container element
    this.paeImgContainer = d3.select('.pae-img-box');

    const borderEle = d3.select('.pae-img-box-border');
    this.paeImgContainer.on('mouseover', () => {
      borderEle.attr("class", "pae-img-box-border active");
    });
    this.paeImgContainer.on('mouseout', () => {
      borderEle.attr("class", "pae-img-box-border");
    });

    // SVG addition
    this.dimensionCalc();
    this.initScales();
    this.updateViewport();

  }

  dimensionCalc() {
    const paeBoxDims = this.paeImgContainer.node().getBoundingClientRect();
    this.width = paeBoxDims.width;
    this.height = paeBoxDims.width;
  }

  initScales() {
    // Scales
    this.x = d3.scaleLinear().domain([0, this.domainMax]).range([0, this.width]); //.nice();
    this.y = d3.scaleLinear().domain([0, this.domainMax]).range([0, this.height]); //.nice();

    // Axis
    this.xAxis = d3.axisBottom(this.x).ticks(6).tickFormat(this.formatTicks).tickSizeOuter(0);
    this.yAxis = d3.axisLeft(this.y).ticks(6).tickFormat(this.formatTicks).tickSizeOuter(0);
  }

  updateViewport() {

    // Create SVGs
    const axisContainer = d3.select('.pae-axis-box');

    const xAxisSvg = axisContainer.append('svg:svg')
    .attr('width', this.width + 7)
    .attr('height', 30)
    .attr('class', 'pae-x-axis')
    .style('z-index', '1')
    .append('g')
    .attr('transform', `translate(5,0)`);

    const yAxisSvg = axisContainer.append('svg:svg')
    .attr('width', 30)
    .attr('height', this.height + 7)
    .attr('class', 'pae-y-axis')
    .style('z-index', '1')
    .append('g')
    .attr('transform', `translate(35,7)`);

    // Add Axis
    xAxisSvg.append('g').call(this.xAxis);
    yAxisSvg.append('g').call(this.yAxis);
   
  }

  toggleRemoveIcon(hide?: boolean) {

    const closeIconElm = this.el.nativeElement.querySelector('.pae-clear-icon');
    if(hide) {
      closeIconElm.style.display = 'none';
    } else {

      const selectionBoxElm = this.el.nativeElement.querySelector('.selection');
      if(!selectionBoxElm || selectionBoxElm.width.baseVal.value === 0) return;

      const leftAdjustVal =  selectionBoxElm.width.baseVal.value > 20 ? 9 : 0;
      let leftMargin = selectionBoxElm.x.baseVal.value + selectionBoxElm.width.baseVal.value - leftAdjustVal;
      let topMargin = selectionBoxElm.y.baseVal.value - 9;

      if(leftMargin >= this.width - 15) {
        leftMargin -= 10;
      }

      if(selectionBoxElm.y.baseVal.value <= 15) {
        topMargin = 2;
      }
      
      closeIconElm.style.marginLeft = leftMargin + 'px';
      closeIconElm.style.marginTop = topMargin + 'px';
      closeIconElm.style.display = 'inline-block';

    }
    
  }

  keyPressListener(event: KeyboardEvent) {
    if (event.key === 'Escape'){
      this.clearSelection();
    }
  }

  clearSelection() {
    this.brushSelection.call(this.brushCtx.move, null);
  }

}
